import { logChatGptOutput } from '@src/utility/datadog';
import { AIPromptLogger } from '@src/monitoring/utils/AIPromptLogger';

import ChatGPTService from './ChatGPTService';
import { buildAdjustedCountInstruction } from '../utility/revisionLengthParser';

import type {
  AIPromptRepository,
  CaptionRepository,
  StoryRepository,
} from '@src/repositories';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { CaptionData, Platform } from '@src/types.ts/caption';
import type {
  Caption,
  ShareableImagePreviewType,
  ShareableImageType,
  VideoClip,
} from '@src/types.ts/story';

type Label = 'otherVideos' | 'shareableImages';

export default class CaptionService extends ChatGPTService {
  private storyRepository: StoryRepository;
  private captionRepository: CaptionRepository;

  constructor(
    videoCreator: IVideoCreatorStore,
    storyRepository: StoryRepository,
    captionRepository: CaptionRepository,
    aiPromptRepository: AIPromptRepository,
  ) {
    // TODO: DatoClientStore integration
    super(videoCreator, aiPromptRepository);

    this.storyRepository = storyRepository;
    this.captionRepository = captionRepository;
  }

  public getExistingCaptionData<T extends Label>(
    data: Pick<CaptionData<T>, 'platform' | 'contentId' | 'label'>,
  ) {
    const content = (
      this.videoCreator.story?.[data.label] as (
        | VideoClip
        | ShareableImageType
      )[]
    )?.find(c => {
      return c.id === data.contentId;
    });

    const platformData = content?._allReferencingCaptions?.find(
      r => r.platform === data.platform,
    );
    return { platformData, content };
  }

  public getExistingCaptionText<T extends Label>(
    data: Pick<CaptionData<T>, 'platform' | 'contentId' | 'label'>,
  ) {
    const { platformData } = this.getExistingCaptionData(data);
    return platformData?.caption || '';
  }

  private getPromptLabel(label: Label) {
    if (label === 'otherVideos') {
      return 'Social Posts';
    }
    return 'Sharable Image Caption';
  }

  private getFullTranscription = () => {
    return this.transcriptionStore
      .getFinalTranscriptionElements()
      ?.map(e => e.value || '')
      .join('');
  };

  private getRequestInputData = async <T extends Label>(
    customInstructions: string | null,
    data: CaptionData<T>,
  ) => {
    const transcript = data.useFullTranscription
      ? this.getFullTranscription()
      : data.transcriptionText || this.getFullTranscription();

    let promptKey = this.getPromptLabel(data.label);
    const aiPrompt = await this.fetchAiPrompt(promptKey);
    if (!aiPrompt || !transcript || !this.videoCreator.storyId) return;

    const field = aiPrompt.promptFields.find(
      f => f.name.toLowerCase() === data.platform,
    );

    let systemMessage = aiPrompt.description + (field?.description || '');

    if (customInstructions) {
      systemMessage += `${systemMessage ? '\n\n' : ''}${customInstructions}`;
    }

    const brandVoice = this.videoCreator.organization?.branding?.brandVoice;
    if (brandVoice) {
      const brandVoicePrompt = await this.fetchAiPrompt('Brand Voice');
      if (brandVoicePrompt) {
        systemMessage += `${systemMessage ? '\n\n' : ''}${
          brandVoicePrompt.description
        }\nBrand voice: ${brandVoice}`;
      }
    }

    const avoidWords = this.videoCreator.organization?.branding?.avoidWords;
    if (avoidWords) {
      const avoidWordsPrompt = await this.fetchAiPrompt('Avoid Words');
      if (avoidWordsPrompt) {
        systemMessage += `${systemMessage ? '\n\n' : ''}${
          avoidWordsPrompt.description
        }\nAvoid words: ${avoidWords}`;
      }
    }

    const countInstruction = buildAdjustedCountInstruction(
      aiPrompt.targetWordCount,
      aiPrompt.targetCharacterCount,
    );
    if (countInstruction) {
      systemMessage += `${systemMessage ? '\n\n' : ''}${countInstruction}`;
    }

    return {
      systemMessage,
      transcript,
    };
  };

  public async saveCaptionsToDato<T extends Label>(data: CaptionData<T>) {
    const story = this.videoCreator?.story;
    if (!story?.id) return;

    const { text } = data;

    if (!data.id) {
      const savedId = await this.captionRepository.createCaption({
        title: `${story.title} - ${data.platform}`,
        platform: data.platform,
        text: text,
        storyId: story.id,
        shareableId: data.contentId,
      });
      this.updateCaptionInStore({ ...data, id: savedId });
      return savedId;
    } else {
      await this.captionRepository.updateCaption(data.id, {
        text: text,
        shareableId: data.contentId,
      });
      this.updateCaptionInStore(data);
    }
  }

  public async regenerateCaptionStreamResponse<T extends Label>(
    customInstructions: string | null = null,
    toggleLoading: (e: boolean) => void,
    data: CaptionData<T>,
    callback: (savedId: string) => void,
    inputData: Record<'systemMessage' | 'transcript', string> | null = null,
  ) {
    const controller = new AbortController();
    const signal = controller.signal;

    const items =
      inputData || (await this.getRequestInputData(customInstructions, data));
    if (!items) return;
    const { systemMessage, transcript } = items;

    const { platformData, content } = this.getExistingCaptionData(data);

    const existingResponse = platformData?.caption || '';

    const storyId = this.videoCreator.story?.id!;

    const logContext = this.getPromptLogContext();
    const promptLog = AIPromptLogger.start(this.datoClient, {
      prompt_title: `Caption — ${data.platform || 'unknown'}`,
      prompt_type: 'caption',
      ...logContext,
      metadata: {
        ...logContext.metadata,
        prompt_title: `Caption — ${data.platform || 'unknown'}`,
        prompt_type: 'Caption',
        endpoint: 'regenerate-gpt-response-stream',
        platform: data.platform,
      },
    });

    try {
      const fetchT0 = performance.now();
      const resPromise = fetch(
        `${process.env.REACT_APP_API_URL}/api/aiprompts/regenerate-gpt-response-stream`,
        {
          signal,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            systemMessage,
            transcript,
            existingResponse,
            storyId,
          }),
        },
      );

      this.updateCaptionInStore({ ...data, text: '' });
      this.streamProcessDone = false;
      const res = await resPromise;
      const firstContentLatencyMs = Math.round(performance.now() - fetchT0);
      if (!res.body) return;

      const reader = res.body.getReader();
      const decoder = new TextDecoder();

      while (!this.streamProcessDone) {
        const { value, done: doneReading } = await reader.read();
        this.streamProcessDone = doneReading;
        let chunk = decoder.decode(value);
        toggleLoading(false);

        const existingResponse = this.getExistingCaptionText(data);
        let text = existingResponse;
        if (
          existingResponse.endsWith('.') &&
          chunk &&
          (!chunk.startsWith(' ') || !chunk.startsWith('\n'))
        ) {
          text += ` ${chunk}`;
        } else {
          text += chunk;
        }
        if (doneReading) {
          logChatGptOutput({ 'regenerate-gpt-response-stream': text });
        }

        this.updateCaptionInStore({ ...data, text });
      }

      if (this.streamProcessDone) {
        if (!content && data.label === 'shareableImages') {
          const savedId = await this.saveShareableImage(data.shareable!);
          data.contentId = savedId;
          callback(savedId!);
        }
        data.text = this.getExistingCaptionText(data);
        await this.saveCaptionsToDato(data);
      }
      promptLog.complete({ provider: 'openai', model_id: 'default', first_content_latency_ms: firstContentLatencyMs });
    } catch (error) {
      console.log('Error: ', error);
      promptLog.fail(error);
    } finally {
      toggleLoading(false);
    }
  }

  public updateCaptionInStore = <T extends Label>(data: CaptionData<T>) => {
    const { id, contentId, label, platform, text } = data;
    if (!contentId) return; // nothing to update

    const story = this.videoCreator.story;
    if (!story) return; // story not yet loaded

    const storyId = this.videoCreator.storyId!;
    const collection = story[label];
    if (!Array.isArray(collection)) return; // defensive – unexpected shape

    let idx = collection.findIndex(content => content.id === contentId);

    // If the target item doesn't exist yet and we're working with shareableImages, create a placeholder.
    if (idx < 0 && label === 'shareableImages') {
      story.shareableImages = [
        ...(story.shareableImages || []),
        {
          id: contentId,
          imagefile: data.shareable?.imageFile,
          quote: data.shareable?.quote || '',
          storytellerName: story.storyTeller?.name || '',
        },
      ] as ShareableImageType[];
      idx = story.shareableImages.length - 1;
    }

    // If still not found (e.g. otherVideos content missing) bail out safely.
    if (idx < 0) return;

    interface CaptionReferencable {
      id: string;
      _allReferencingCaptions?: Caption[];
    }
    const target = story[label][idx] as CaptionReferencable;
    if (!target) return; // extra guard

    // Ensure the captions array exists so later logic is simplified.
    if (!Array.isArray(target._allReferencingCaptions)) {
      target._allReferencingCaptions = [];
    }

    const existing = target._allReferencingCaptions.find(
      (c: Caption) => c.platform === platform,
    );
    const sanitized = text.replace(/^["'\s-]+|["'\s-]+$/g, '')?.trim();

    if (!existing) {
      target._allReferencingCaptions.push({
        id,
        platform,
        story: { id: storyId },
        shareable: {
          __typename:
            label === 'otherVideos' ? 'VideoRecord' : 'ShareableImageRecord',
        },
        caption: sanitized,
      });
    } else {
      target._allReferencingCaptions = target._allReferencingCaptions.map(
        (caption: Caption) =>
          caption.platform === platform
            ? {
                ...caption,
                id,
                shareable: {
                  __typename:
                    label === 'otherVideos'
                      ? 'VideoRecord'
                      : 'ShareableImageRecord',
                },
                caption: sanitized,
              }
            : caption,
      );
    }
  };

  public async saveShareableImage(shareable: ShareableImagePreviewType) {
    const unsavedIdx = this.videoCreator.story!.shareableImages.length - 1;
    const unsaved = this.videoCreator.story!.shareableImages[unsavedIdx];

    const savedId = await this.storyRepository?.createShareableImage(
      this.videoCreator.story!,
      shareable.quote,
      shareable.imageFile,
    );

    this.videoCreator.unsavedShareableImages =
      this.videoCreator.unsavedShareableImages?.filter(
        o => o.id !== unsaved.id,
      ) || [];

    this.videoCreator!.story!.shareableImages[unsavedIdx].id = savedId!;
    await this.videoCreator.updateStory(this.videoCreator.story!);

    return savedId;
  }

  public async generateAllCaptions(
    contentId: string,
    key = 'Social Posts',
    transcriptText?: string,
  ) {
    const transcript = transcriptText || this.getFullTranscription();
    const aiPrompt = await this.fetchAiPrompt(key);
    if (!aiPrompt || !transcript || !this.videoCreator.storyId) return;

    let systemMessage = aiPrompt.description;
    // Add target word count or character count instruction (mutually exclusive, word count takes priority)
    const countInstruction = buildAdjustedCountInstruction(
      aiPrompt.targetWordCount,
      aiPrompt.targetCharacterCount,
    );
    if (countInstruction) {
      systemMessage += `${systemMessage ? '\n\n' : ''}${countInstruction}`;
    }

    await Promise.all(
      aiPrompt.promptFields.map(async f => {
        const platform = f.name.toLowerCase() as Lowercase<Platform>;
        const label =
          key === 'Social Posts' ? 'otherVideos' : ('shareableImages' as Label);

        const captionData = this.getExistingCaptionData({
          platform,
          label,
          contentId,
        });
        if (!captionData.platformData?.caption) {
          const data = {
            platform: f.name.toLowerCase() as Lowercase<Platform>,
            text: '',
            label:
              key === 'Social Posts'
                ? 'otherVideos'
                : ('shareableImages' as Label),
            contentId,
            id: captionData.platformData?.id,
            useFullTranscription: false,
          };

          const captionService = new CaptionService(
            this.videoCreator,
            this.storyRepository,
            this.captionRepository,
            this.aiPromptRepository,
          );
          await captionService.regenerateCaptionStreamResponse(
            '',
            () => {},
            data,
            () => {},
            { systemMessage, transcript },
          );
        }
      }),
    );
  }
}
